import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Paper, Typography, Box, IconButton, Tooltip } from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import { useExpanded } from 'components/layouts/ProtectedLayout';
import { pageStyles } from 'customStyles/common/index';
import { useQueryDashboard } from 'hooks/useQueryDashboard';
import { useDashboardPreferences, SortOrder } from 'hooks/useDashboardPreferences';
import { TransactionQueueSection } from 'components/Dashboard/TransactionQueueSection';
import { DashboardSettingsPanel } from 'components/Dashboard/DashboardSettingsPanel';
import { applyQueueStatusFilter } from 'utils/dashboardDrilldown';

// OOTB queue config — will be driven by contract config in a future ticket
const OOTB_QUEUES = [
    { label: 'Decision Complete', statusValue: 'Decision Complete' },
    { label: 'Waiting for responses', statusValue: 'Waiting for responses' },
    { label: 'Waiting for Manual Adjudication', statusValue: 'Waiting for Manual Adjudication' },
    { label: 'Error', statusValue: 'Error' },
    { label: 'QC Reject', statusValue: 'QC Reject' },
    { label: 'QC Reject NoRBsub', statusValue: 'QC Reject NoRBsub' },
    { label: 'ERRT-Error NoRBsub', statusValue: 'ERRT-Error NoRBsub' },
    { label: 'Pending Adjudication', statusValue: 'Pending Adjudication' },
];

export const DashboardPage: React.FC = () => {
    const { expanded } = useExpanded();
    const navigate = useNavigate();
    const { queueCounts, loading, refresh } = useQueryDashboard();
    const {
        preferences,
        setIsDefaultLandingPage,
        setThresholds,
        setSortOrder,
    } = useDashboardPreferences();

    const [settingsOpen, setSettingsOpen] = useState(false);

    const queues = queueCounts.map((q) => ({
        label: q.transactionStatus,
        statusValue: q.transactionStatus,
    }));

    const handleSortChange = (statusValue: string, order: SortOrder) => {
        setSortOrder(statusValue, order);
    };

    const handleTileClick = (queueLabel: string, statusValue: string) => {
        const sortOrder = preferences.sortOrders[statusValue] ?? 'NEWEST';
        applyQueueStatusFilter(queueLabel, sortOrder);
        navigate('/app/transaction-summary');
    };

    return (
        <Paper
            data-testid="dashboard-page"
            elevation={0}
            sx={{
                ...pageStyles.pagePaperStyle,
                maxWidth: `calc(100vw - ${expanded ? '256px' : '64px'})`,
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 3,
                }}
            >
                <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    Dashboard
                </Typography>
                <Tooltip title="Dashboard Settings">
                    <IconButton
                        data-testid="dashboard-settings-gear-button"
                        onClick={() => setSettingsOpen(true)}
                        aria-label="Open dashboard settings"
                        sx={{ color: 'text.primary' }}
                    >
                        <SettingsIcon />
                    </IconButton>
                </Tooltip>
            </Box>

            <TransactionQueueSection
                queues={OOTB_QUEUES}
                queueCounts={queueCounts}
                loading={loading}
                sortOrders={preferences.sortOrders}
                onSortChange={handleSortChange}
                onRefresh={refresh}
                onTileClick={handleTileClick}
                thresholdLow={preferences.thresholdLow}
                thresholdHigh={preferences.thresholdHigh}
            />

            <DashboardSettingsPanel
                open={settingsOpen}
                onClose={() => setSettingsOpen(false)}
                preferences={preferences}
                onToggleDefaultLandingPage={setIsDefaultLandingPage}
                onThresholdsChange={setThresholds}
            />
        </Paper>
    );
};